/**
 * The ONE answer to "is this pointer/focus target inside a floating overlay?"
 *
 * Every hand-rolled outside-click / Escape handler that keeps an editor open
 * while its picker is up has to distinguish "the user is interacting with a
 * popover" from "the user clicked the page". The popover is PORTALED to
 * `document.body`, so `myRef.contains(target)` says "outside" for both — the
 * handler has to recognise the overlay by its own DOM shape.
 *
 * Four surfaces derived that shape independently within ten days, and every
 * one of them shipped as "typing in the picker's search box closes the
 * editor":
 *
 *   - `useInlineEdit` keyed on `[role="dialog"|"listbox"|"menu"]` — broke the
 *     moment `Dropdown.Popover` went non-modal, because react-aria only gives
 *     a popover root `role="dialog"` when it is MODAL, and the association
 *     picker's search input sits beside (not inside) its `role="menu"` list.
 *   - the memo composer (now `useInlineMemoAutosave`) keyed on `[data-placement]` after its role-based check
 *     matched the page's contacts grid and swallowed the click.
 *   - `CardFieldsPopover` keyed on `[data-trigger]`.
 *   - The pipeline card fixed a different leak (React synthetic events
 *     bubbling through the portal — see `PortalEventBoundary`).
 *
 * This module owns the definition. Consumers call the predicate; they never
 * spell a react-aria stamp or an overlay role themselves — an ESLint
 * `no-restricted-syntax` selector in `eslint.config.mjs` rejects those string
 * literals outside `lib/base`, and `__tests__/floating-overlay.test.tsx`
 * exercises the predicate against the REAL `Dropdown.Popover`, `Popover` and
 * `ComboBox`, so a react-aria upgrade that renames a stamp, or a modality flip
 * on one of our wrappers, fails there instead of in production.
 */

/**
 * What counts as a floating overlay root, nearest-ancestor wins:
 *
 * - `[data-trigger]` / `[data-placement]`: react-aria stamps both on every
 *   positioned overlay root (Popover, Tooltip) regardless of modality — the
 *   only marker a non-modal popover carries. Both are documented render-prop
 *   data attributes of react-aria-components, not internals.
 * - ARIA overlay roles: react-aria modal dialogs (`Modal`, `ModalOverlay`,
 *   the slideouts) and the hand-rolled floating lists that predate `lib/base`
 *   (recipient pickers, context menus, calendar search).
 *
 * Deliberately NOT here: `[data-keep-overlay-open]` (the slideout's own
 * "don't dismiss me" escape hatch — a different question) and `[role="grid"]`
 * / `[role="listbox"]` on page-level lists is accepted as a cost: every such
 * `listbox` in the app today is a floating dropdown.
 */
const FLOATING_OVERLAY_ROOT_SELECTOR = [
  '[data-trigger]',
  '[data-placement]',
  '[role="dialog"]',
  '[role="alertdialog"]',
  '[role="listbox"]',
  '[role="menu"]',
].join(', ');

/**
 * The nearest floating-overlay root containing `target`, or `null` when the
 * target is plain page content. Accepts anything an event or
 * `document.activeElement` hands you (text nodes resolve to their parent).
 */
export function findFloatingOverlay(
  target: EventTarget | Node | null | undefined
): Element | null {
  if (!(target instanceof Node)) {
    return null;
  }
  const element =
    target instanceof Element ? target : (target.parentElement ?? null);
  return element?.closest(FLOATING_OVERLAY_ROOT_SELECTOR) ?? null;
}

export interface IsInsideFloatingOverlayOptions {
  /**
   * The element asking — an inline editor, a form, a panel. An overlay that
   * CONTAINS it is that element's own page (the record slideout around an
   * inline-editable row), not a floating layer above it, so the answer is
   * `false` for clicks elsewhere in that overlay. Overlays that do not contain
   * it (a picker popover portaled to `body`, a create-record modal opened from
   * the picker) are floating layers and answer `true`.
   */
  surface?: Element | null;
}

/**
 * Whether `target` is inside a floating overlay other than the one `surface`
 * itself lives in. Use this — not a role or data-attribute selector — in any
 * `document`-level pointerdown / keydown handler that would otherwise treat
 * "not inside my ref" as "dismiss / commit me".
 */
export function isInsideFloatingOverlay(
  target: EventTarget | Node | null | undefined,
  { surface }: IsInsideFloatingOverlayOptions = {}
): boolean {
  const overlay = findFloatingOverlay(target);
  if (!overlay) {
    return false;
  }
  if (surface && overlay.contains(surface)) {
    return false;
  }
  return true;
}
